import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ZStudioNav from '@/pages/dashboard/apps/zstudio/components/ZStudioNav';
import { useAuth } from '@/hooks/useAuth';
import {
  listZProjects,
  listZCreations,
  listZGenerations,
  Z_MEDIA_KIND_META,
  type ZStudioProject,
  type ZStudioCreation,
  type ZStudioGeneration,
} from '@/lib/zstudio';

const ACTIONS: { key: string; type: string; label: string; desc: string; icon: string; accent: string }[] = [
  { key: 'image', type: 'image', label: 'Créer une image', desc: 'Visuel pro à partir d’une idée', icon: 'ri-image-add-line', accent: 'bg-accent-100 text-accent-700' },
  { key: 'sketch', type: 'fashion', label: 'Transformer un croquis', desc: 'Croquis → photo de mode réaliste', icon: 'ri-pencil-ruler-2-line', accent: 'bg-primary-100 text-primary-700' },
  { key: 'video', type: 'video', label: 'Créer une vidéo', desc: 'Vidéo IA courte et animée', icon: 'ri-film-line', accent: 'bg-secondary-100 text-secondary-800' },
  { key: 'ad', type: 'ad', label: 'Créer une publicité', desc: 'Texte + visuel + CTA', icon: 'ri-megaphone-line', accent: 'bg-accent-100 text-accent-700' },
  { key: 'social', type: 'social', label: 'Créer une publication', desc: 'Caption + hashtags réseaux', icon: 'ri-share-circle-line', accent: 'bg-primary-100 text-primary-700' },
  { key: 'product', type: 'product', label: 'Utiliser un produit Zifek', desc: 'Depuis votre catalogue', icon: 'ri-shopping-bag-3-line', accent: 'bg-secondary-100 text-secondary-800' },
  { key: 'service', type: 'service', label: 'Utiliser un service Zifek', desc: 'Depuis vos services', icon: 'ri-service-line', accent: 'bg-accent-100 text-accent-700' },
  { key: 'campaign', type: 'campaign', label: 'Campagne complète', desc: 'Concept + médias + textes', icon: 'ri-rocket-line', accent: 'bg-primary-100 text-primary-700' },
];

// Outils avancés (spec 67) — accès direct aux studios spécialisés.
const ADVANCED_TOOLS: { path: string; label: string; desc: string; icon: string; accent: string }[] = [
  { path: '/dashboard/zstudio/tryon', label: 'Essayage virtuel', desc: 'Vêtement réel → mannequin', icon: 'ri-user-star-line', accent: 'bg-accent-100 text-accent-700' },
  { path: '/dashboard/zstudio/lookbook', label: 'Lookbook IA', desc: 'Catalogue de collection', icon: 'ri-book-open-line', accent: 'bg-primary-100 text-primary-700' },
  { path: '/dashboard/zstudio/edit', label: 'Édition IA', desc: 'Retoucher · varier une image', icon: 'ri-equalizer-2-line', accent: 'bg-secondary-100 text-secondary-800' },
  { path: '/dashboard/zstudio/qr', label: 'QR produit', desc: 'QR vers produit / service', icon: 'ri-qr-code-line', accent: 'bg-background-100 text-foreground-700' },
];

// Presets de démarrage rapide (spec 67) — lancement direct depuis l'accueil.
const QUICK_PRESETS: { key: string; label: string; desc: string; icon: string; accent: string }[] = [
  { key: 'product-pro', label: 'Photo produit pro', desc: 'Image carrée, fond studio', icon: 'ri-camera-lens-line', accent: 'bg-accent-100 text-accent-700' },
  { key: 'before-after', label: 'Before / After', desc: 'Transformation avant/après', icon: 'ri-contrast-2-line', accent: 'bg-primary-100 text-primary-700' },
  { key: 'reel-vertical', label: 'Reel vertical', desc: 'Vidéo courte 9:16', icon: 'ri-smartphone-line', accent: 'bg-secondary-100 text-secondary-800' },
];

const GEN_TYPE_META: Record<string, { label: string; icon: string; className: string }> = {
  image: { label: 'Image', icon: 'ri-image-line', className: 'bg-accent-100 text-accent-700' },
  video: { label: 'Vidéo', icon: 'ri-film-line', className: 'bg-primary-100 text-primary-700' },
  campaign: { label: 'Campagne', icon: 'ri-rocket-line', className: 'bg-primary-100 text-primary-700' },
  ad_copy: { label: 'Texte pub', icon: 'ri-megaphone-line', className: 'bg-secondary-100 text-secondary-800' },
  hashtags: { label: 'Hashtags', icon: 'ri-hashtag', className: 'bg-secondary-100 text-secondary-800' },
  slogan: { label: 'Slogan', icon: 'ri-quill-pen-line', className: 'bg-secondary-100 text-secondary-800' },
  tryon: { label: 'Try-on', icon: 'ri-user-star-line', className: 'bg-accent-100 text-accent-700' },
  edit_image: { label: 'Édition IA', icon: 'ri-equalizer-2-line', className: 'bg-accent-100 text-accent-700' },
  edit: { label: 'Édition IA', icon: 'ri-equalizer-2-line', className: 'bg-accent-100 text-accent-700' },
  lookbook: { label: 'Lookbook', icon: 'ri-book-open-line', className: 'bg-secondary-100 text-secondary-800' },
};

export default function ZStudioHomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ZStudioProject[]>([]);
  const [creations, setCreations] = useState<ZStudioCreation[]>([]);
  const [generations, setGenerations] = useState<ZStudioGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [p, c, g] = await Promise.all([
        listZProjects(user.id),
        listZCreations(user.id),
        listZGenerations(user.id, 4),
      ]);
      setProjects(p);
      setCreations(c);
      setGenerations(g);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  // Relancer une génération en un clic depuis l'accueil.
  const relaunch = (g: ZStudioGeneration) => {
    const t = (g.type || '').toLowerCase();
    if (t === 'tryon') { navigate('/dashboard/zstudio/tryon'); return; }
    if (t === 'edit_image' || t === 'edit') { navigate('/dashboard/zstudio/edit'); return; }
    if (t === 'lookbook') { navigate('/dashboard/zstudio/lookbook'); return; }
    const kind = t.includes('video') ? 'video'
      : t.includes('campaign') ? 'campaign'
        : (t.includes('ad') || t.includes('slogan')) ? 'ad'
          : (t.includes('hashtag') || t.includes('caption') || t.includes('social')) ? 'social'
            : 'image';
    const search = new URLSearchParams({ type: kind, mode: kind });
    if (g.prompt) {
      search.set('subject', g.prompt.slice(0, 110));
      search.set('brief', g.prompt);
    }
    navigate(`/dashboard/zstudio/wizard?${search.toString()}`);
  };

  const counts = {
    images: creations.filter((c) => c.media_kind === 'image').length,
    videos: creations.filter((c) => c.media_kind === 'video').length,
    texts: creations.filter((c) => c.media_kind === 'text').length,
    favorites: creations.filter((c) => c.favorite).length,
  };

  const recent = creations.slice(0, 8);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-magic-line text-accent-500"></i>ZStudio
          </h2>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Le studio créatif IA : images, vidéos, publicités, publications, campagnes — à partir de vos produits et services.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/dashboard/zstudio/history"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-background-200/70 text-sm font-medium text-foreground-700 whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-history-line"></i>Historique
          </Link>
          <Link
            to="/dashboard/zstudio/creations"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-background-200/70 text-sm font-medium text-foreground-700 whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-archive-2-line"></i>Mes créations
          </Link>
        </div>
      </div>

      <ZStudioNav />

      {/* Presets rapides (spec 67) — accès direct depuis l'accueil */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-foreground-900 flex items-center gap-2 mb-3">
          <i className="ri-flashlight-line text-accent-500"></i>Démarrage rapide
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {QUICK_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              onClick={() => navigate(`/dashboard/zstudio/wizard?preset=${preset.key}`)}
              className="group flex items-center gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-300 transition-colors cursor-pointer text-left"
            >
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${preset.accent}`}>
                <i className={`${preset.icon} text-lg`}></i>
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-foreground-900">{preset.label}</span>
                <span className="block text-xs text-foreground-500 mt-0.5">{preset.desc}</span>
              </span>
              <i className="ri-arrow-right-line text-foreground-300 ml-auto group-hover:text-accent-500 transition-colors"></i>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {/* Actions principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {ACTIONS.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => navigate(`/dashboard/zstudio/wizard?type=${a.type}&mode=${a.key}`)}
            className="group flex flex-col items-start text-left gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
          >
            <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${a.accent}`}>
              <i className={`${a.icon} text-lg`}></i>
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-foreground-900">{a.label}</span>
              <span className="block text-xs text-foreground-500 mt-0.5">{a.desc}</span>
            </span>
          </button>
        ))}
      </div>

      {/* Outils avancés (spec 67) */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-foreground-900 flex items-center gap-2 mb-3">
          <i className="ri-shape-2-line text-foreground-500"></i>Outils avancés
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {ADVANCED_TOOLS.map((t) => (
            <Link
              key={t.path}
              to={t.path}
              className="group flex flex-col items-start text-left gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.accent}`}>
                <i className={`${t.icon} text-lg`}></i>
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-foreground-900">{t.label}</span>
                <span className="block text-xs text-foreground-500 mt-0.5">{t.desc}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Images', value: counts.images, icon: 'ri-image-line' },
          { label: 'Vidéos', value: counts.videos, icon: 'ri-film-line' },
          { label: 'Textes', value: counts.texts, icon: 'ri-file-text-line' },
          { label: 'Favoris', value: counts.favorites, icon: 'ri-star-line' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-xl px-4 py-3 flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-500 flex items-center justify-center">
              <i className={s.icon}></i>
            </span>
            <div>
              <p className="text-lg font-bold text-foreground-900 leading-none">{s.value}</p>
              <p className="text-[11px] text-foreground-500 mt-1">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Créations récentes */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
            <i className="ri-history-line text-foreground-500"></i>Créations récentes
          </h3>
          {creations.length > 0 && (
            <Link to="/dashboard/zstudio/creations" className="text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
              Tout voir
            </Link>
          )}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i></div>
        ) : recent.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 bg-background-50 border border-dashed border-background-300/70 rounded-xl text-center">
            <span className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-magic-line text-xl text-foreground-400"></i>
            </span>
            <p className="text-sm font-medium text-foreground-700">Aucune création pour l’instant</p>
            <p className="text-xs text-foreground-500 mt-1">Lancez une action ci-dessus pour commencer.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {recent.map((c) => {
              const meta = Z_MEDIA_KIND_META[c.media_kind || ''] || Z_MEDIA_KIND_META.text;
              return (
                <div key={c.id} className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden">
                  <div className="aspect-square bg-background-100 flex items-center justify-center overflow-hidden">
                    {c.thumbnail_url || (c.media_kind === 'image' && c.url) ? (
                      <img src={c.thumbnail_url || (c.url as string)} alt={c.title || 'Création'} className="w-full h-full object-cover object-top" />
                    ) : c.media_kind === 'video' && c.url ? (
                      <video src={c.url} muted className="w-full h-full object-cover object-top" />
                    ) : (
                      <i className={`${meta.icon} text-3xl text-foreground-400`}></i>
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-medium text-foreground-800 truncate">{c.title || 'Sans titre'}</p>
                    <span className={`inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-full ${meta.className}`}>
                      <i className={meta.icon}></i>{meta.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dernières générations (spec 56) — relance en un clic */}
      {generations.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
              <i className="ri-time-line text-foreground-500"></i>Dernières générations
            </h3>
            <Link to="/dashboard/zstudio/history" className="text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer">Historique complet</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {generations.map((g) => {
              const meta = GEN_TYPE_META[(g.type || '').toLowerCase()] || GEN_TYPE_META.image;
              const out = g.output as Record<string, unknown> | null;
              const url = out && typeof out.url === 'string' ? out.url : null;
              return (
                <div key={g.id} className="flex flex-col bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                  <div className="aspect-video bg-background-100 flex items-center justify-center overflow-hidden">
                    {url && (g.type || '').toLowerCase().includes('video') ? (
                      <video src={url} muted className="w-full h-full object-cover object-top" />
                    ) : url ? (
                      <img src={url} alt={g.prompt || 'Génération'} className="w-full h-full object-cover object-top" />
                    ) : (
                      <i className={`${meta.icon} text-2xl text-foreground-400`}></i>
                    )}
                  </div>
                  <div className="p-3 flex flex-col gap-2 flex-1">
                    <span className={`inline-flex w-fit items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${meta.className}`}>
                      <i className={meta.icon}></i>{meta.label}
                    </span>
                    <p className="text-xs text-foreground-600 line-clamp-2 break-words flex-1">{g.prompt || 'Sans description'}</p>
                    <button type="button" onClick={() => relaunch(g)} className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
                      <i className="ri-refresh-line"></i>Relancer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Projets */}
      {projects.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-foreground-900 flex items-center gap-2 mb-3">
            <i className="ri-folders-line text-foreground-500"></i>Projets récents
          </h3>
          <div className="space-y-2">
            {projects.slice(0, 6).map((p) => (
              <div key={p.id} className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg px-4 py-3">
                <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-500 flex items-center justify-center">
                  <i className="ri-folder-3-line"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-800 truncate">{p.name}</p>
                  <p className="text-xs text-foreground-500 truncate">{p.kind} · {p.aspect_ratio}{p.style ? ` · ${p.style}` : ''}</p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">{creations.filter((c) => c.project_id === p.id).length} création(s)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}